import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { submissionApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { formatDate, formatFileSize, getRiskColor, getStatusColor } from '../lib/utils';
import { 
  Upload, 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  ArrowRight,
  TrendingUp,
  Loader2
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    processing: 0,
    avgScore: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await submissionApi.list(0, 5);
        setSubmissions(response.data);
        
        // Calculate stats
        const completed = response.data.filter(s => s.status === 'completed');
        const processing = response.data.filter(s => s.status === 'processing' || s.status === 'pending');
        const avgScore = completed.length > 0 
          ? completed.reduce((acc, s) => acc + (s.similarity_score || 0), 0) / completed.length 
          : 0;
        
        setStats({
          total: response.data.length,
          completed: completed.length,
          processing: processing.length,
          avgScore: avgScore.toFixed(1)
        });
      } catch (error) {
        console.error('Failed to fetch submissions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    
    // Poll for updates
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const statCards = [
    { label: 'Total Submissions', value: stats.total, icon: FileText, color: 'bg-blue-100 text-blue-700' },
    { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
    { label: 'Processing', value: stats.processing, icon: Clock, color: 'bg-amber-100 text-amber-700' },
    { label: 'Avg Similarity', value: `${stats.avgScore}%`, icon: TrendingUp, color: 'bg-purple-100 text-purple-700' }
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="student-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-bold text-foreground">
              Welcome back, {user?.full_name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              Monitor your submissions and check new documents
            </p>
          </div>
          <Link to="/upload">
            <Button size="lg" className="shadow-md" data-testid="upload-new-btn">
              <Upload className="w-4 h-4 mr-2" />
              Upload New Document
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.label}</p>
                      <p className="font-mono text-3xl font-bold text-foreground mt-1">
                        {stat.value}
                      </p>
                    </div>
                    <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${stat.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Submissions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between border-b border-border/40">
            <CardTitle className="font-serif">Recent Submissions</CardTitle>
            <Link to="/history">
              <Button variant="ghost" size="sm" data-testid="view-all-submissions-btn">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12 px-4">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-semibold text-foreground mb-2">No submissions yet</h3>
                <p className="text-muted-foreground mb-4">
                  Upload your first document to get started
                </p>
                <Link to="/upload">
                  <Button data-testid="first-upload-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {submissions.map((submission) => (
                  <Link
                    key={submission.id}
                    to={`/submission/${submission.id}`}
                    className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                    data-testid={`submission-row-${submission.id}`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-medium text-foreground truncate">{submission.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {submission.file_name} • {formatFileSize(submission.file_size)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      {submission.status === 'completed' && submission.similarity_score !== null && (
                        <div className="text-right hidden sm:block">
                          <p className="font-mono text-lg font-bold text-foreground">
                            {submission.similarity_score?.toFixed(1)}%
                          </p>
                          <Badge className={getRiskColor(submission.risk_level)} variant="secondary">
                            {submission.risk_level || 'N/A'}
                          </Badge>
                        </div>
                      )}
                      
                      <Badge className={getStatusColor(submission.status)} variant="secondary">
                        {submission.status === 'processing' && (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        )}
                        {submission.status}
                      </Badge>
                      
                      <span className="text-sm text-muted-foreground hidden md:block">
                        {formatDate(submission.created_at)}
                      </span>
                      
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-accent/20 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Upload className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Submit New Document</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Upload PDF, DOCX, or TXT files for plagiarism analysis
                  </p>
                  <Link to="/upload">
                    <Button variant="outline" size="sm" data-testid="quick-upload-btn">
                      Upload Now
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">View History</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Access all your past submissions and download reports
                  </p>
                  <Link to="/history">
                    <Button variant="outline" size="sm" data-testid="quick-history-btn">
                      View History
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
